import './fonts.css'
import { createTheme } from '@mui/material/styles'
import { TBetOperator } from '@pturf-betin/betin-models-lib'
import { CSSProperties } from 'react'

interface ICustomTypographyVariants {
    'lexend-12-semi-bold'?: CSSProperties
    'lexend-14-semi-bold'?: CSSProperties
    'lexend-16-semi-bold'?: CSSProperties
    'lexend-18-semi-bold'?: CSSProperties
    'lexend-12-bold'?: CSSProperties
    'lexend-14-bold'?: CSSProperties
    'lexend-16-bold'?: CSSProperties
    'lexend-18-bold'?: CSSProperties

    'title-48-bold'?: CSSProperties
    'title-24-semi-bold'?: CSSProperties
    'title-16-bold'?: CSSProperties
    'title-14-bold'?: CSSProperties

    'subtitle-18-bold'?: CSSProperties
    'subtitle-16-semi-bold'?: CSSProperties

    'text-16-regular'?: CSSProperties
    'text-14-regular'?: CSSProperties
    'text-13-semi-bold'?: CSSProperties
    'text-13-regular'?: CSSProperties
    'text-12-semi-bold'?: CSSProperties
    'text-12-regular'?: CSSProperties

    'odds-64-semi-bold'?: CSSProperties
    'odds-24-bold'?: CSSProperties
    'odds-16-bold'?: CSSProperties

    'numeric-32-regular'?: CSSProperties
    'numeric-21-semi-bold'?: CSSProperties
    'numeric-15-semi-bold'?: CSSProperties
    'numeric-14-semi-bold'?: CSSProperties
    'numeric-13-semi-bold'?: CSSProperties
}

type TOperatorTheme = {
    [operator in TBetOperator]?: {
        backgroundColor: string
        hoverBackgroundColor: string
        primary: string
    }
}

declare module '@mui/material/styles' {
    interface Theme {
        operators: TOperatorTheme
    }
    interface TypographyVariants extends ICustomTypographyVariants {}

    // Allow configuration using `createTheme`
    interface ThemeOptions {
        operators?: TOperatorTheme
    }
    interface TypographyVariantsOptions extends ICustomTypographyVariants {}
}

declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        'title-48-bold': true
        'title-24-semi-bold': true
        'title-18-extra-bold': false // not used
        'title-16-bold': true
        'title-14-bold': true

        'subtitle-18-bold': true
        'subtitle-16-semi-bold': true

        'text-16-regular': true
        'text-14-regular': true
        'text-13-semi-bold': true
        'text-13-regular': true
        'text-12-semi-bold': true
        'text-12-regular': true
    }
}

export const lineHeight = 32
export const betinTheme = createTheme({
    operators: {
        'PMU.fr': {
            backgroundColor: '#004F49',
            hoverBackgroundColor: '#003632',
            primary: '#004F49'
        },
        GENYBET: {
            backgroundColor: '#FF9B00',
            hoverBackgroundColor: '#F27400',
            primary: '#00a591'
        },
        PMU: {
            backgroundColor: '#004F49',
            hoverBackgroundColor: '#003632',
            primary: '#004F49'
        }
    },
    palette: {
        primary: {
            main: '#00a591',
            contrastText: '#FFFFFF'
        },
        secondary: {
            main: '#ed7525'
        },
        error: {
            light: '#DC3C34',
            main: '#E62314',
            dark: '#A11A0F'
        },
        warning: {
            light: '#FFE145',
            main: '#FFE500',
            contrastText: '#353E4A'
        },
        success: {
            light: '#30A69A',
            main: '#2D9766',
            dark: '#02692D'
        },
        divider: '#D4DDE0',
        text: {
            primary: '#000',
            secondary: '#52525D'
        },
        background: {
            default: '#FFFFFF',
            paper: '#FAFCFF'
        }
    },
    typography: {
        fontFamily: ["'Manrope'", 'sans-serif'].join(','), // Do not forget to import the fonts manually
        button: {
            borderRadius: '2px'
        },
        'title-48-bold': {
            fontSize: 48,
            fontWeight: 700,
            fontFamily: 'Manrope',
            lineHeight: '130%'
        },
        'title-24-semi-bold': {
            fontSize: 24,
            fontWeight: 600,
            fontFamily: 'Manrope',
            lineHeight: '130%'
        },
        'title-16-bold': {
            fontSize: 16,
            fontWeight: 700,
            fontFamily: 'Manrope',
            lineHeight: '130%'
        },
        'title-14-bold': {
            fontSize: 14,
            fontWeight: 700,
            fontFamily: 'Manrope',
            lineHeight: '130%'
        },

        'subtitle-18-bold': {
            fontSize: 18,
            fontWeight: 700,
            fontFamily: 'Manrope',
            lineHeight: '130%'
        },
        'subtitle-16-semi-bold': {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'Manrope',
            lineHeight: '130%'
        },

        'text-16-regular': {
            fontSize: 16,
            fontWeight: 400,
            fontFamily: 'Manrope',
            lineHeight: '130%'
        },
        'text-14-regular': {
            fontSize: 14,
            fontWeight: 400,
            fontFamily: 'Manrope',
            lineHeight: '130%'
        },
        'text-13-semi-bold': {
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'Manrope',
            lineHeight: '130%'
        },
        'text-13-regular': {
            fontSize: 13,
            fontWeight: 400,
            fontFamily: 'Manrope',
            lineHeight: '130%'
        },
        'text-12-semi-bold': {
            fontSize: 12,
            fontWeight: 600,
            fontFamily: 'Manrope',
            lineHeight: '130%'
        },
        'text-12-regular': {
            fontSize: 12,
            fontWeight: 400,
            fontFamily: 'Manrope',
            lineHeight: '130%'
        }
    },
    components: {
        MuiInputLabel: {
            defaultProps: {
                sx: {
                    fontSize: '14px',
                    fontFamily: 'Inter',
                    fontWeight: 400
                }
            }
        },
        MuiOutlinedInput: {
            defaultProps: {
                sx: {
                    fontSize: '14px',
                    fontFamily: 'Manrope',
                    fontWeight: 400
                }
            },
            styleOverrides: {
                root: {
                    '& input[type=number]': {
                        appearance: 'textfield'
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                        appearance: 'none',
                        margin: 0
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                        appearance: 'none',
                        margin: 0
                    },
                    '&:hover fieldset': {
                        borderColor: '#4E5F68'
                    },

                    '&.Mui-focused fieldset': {
                        borderColor: '#F3087E',
                        borderWidth: 2
                    },

                    '&.Mui-error fieldset': {
                        borderColor: '#E62314'
                    }
                }
            }
        }
    }
})
